/**
 * Conversion ROI — joins AI cost events with conversion events to produce
 * per-feature ROI verdicts (PROFITABLE / BREAK_EVEN / KILL / NO_OUTCOME_DATA).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { TrendingUp, Skull, Plus, Trophy } from 'lucide-react';
import PostHogPipelineWizard from '../../components/PostHogPipelineWizard';
import MixpanelPipelineWizard from '../../components/MixpanelPipelineWizard';
import AmplitudePipelineWizard from '../../components/AmplitudePipelineWizard';
import SegmentAdapterWizard from '../../components/SegmentAdapterWizard';
import HeapAdapterWizard from '../../components/HeapAdapterWizard';
import Week1RoiCard from '../../components/Week1RoiCard';
import PilotToPaidCard from '../../components/PilotToPaidCard';
import DemoFunnelTile from '../../components/DemoFunnelTile';

const API = process.env.REACT_APP_BACKEND_URL;

const VerdictPill = ({ verdict }) => {
  const map = {
    PROFITABLE: { cls: 'bg-emerald-500/15 text-emerald-300', label: 'PROFITABLE · 3×+' },
    BREAK_EVEN: { cls: 'bg-amber-500/15 text-amber-300', label: 'BREAK-EVEN · 1-3×' },
    KILL: { cls: 'bg-rose-500/15 text-rose-300', label: 'KILL · <1×' },
    NO_OUTCOME_DATA: { cls: 'bg-slate-500/15 text-slate-400', label: 'NO OUTCOME' },
  };
  const s = map[verdict] || map.NO_OUTCOME_DATA;
  return <span className={`inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest ${s.cls}`}>{s.label}</span>;
};

export default function ConversionROI() {
  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);
  const [killList, setKillList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seedBusy, setSeedBusy] = useState(false);
  const [newEvent, setNewEvent] = useState({ feature_slug: '', outcome_type: 'conversion', value_usd: 0 });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [rRes, kRes] = await Promise.all([
        fetch(`${API}/api/conversion-join/roi-report?days=${days}`, { headers }),
        fetch(`${API}/api/conversion-join/kill-list?days=${days}`, { headers }),
      ]);
      setReport(await rRes.json());
      setKillList(await kRes.json());
    } finally { setLoading(false); }
  }, [days]);
  useEffect(() => { refresh(); }, [refresh]);

  const submitEvent = async () => {
    if (!newEvent.feature_slug) return;
    setSeedBusy(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/conversion-join/event`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newEvent),
      });
      setNewEvent({ feature_slug: '', outcome_type: 'conversion', value_usd: 0 });
      refresh();
    } finally { setSeedBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 -mt-px"><div className="max-w-6xl mx-auto px-6 py-8" data-testid="conversion-roi-page">
      <div className="mb-10">
        <div className="flex items-start gap-4 mb-4">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-emerald-500 bg-gradient-to-br from-emerald-500 to-cyan-500 items-center justify-center shadow-xl shadow-emerald-500/40 shrink-0">
            <Trophy className="w-7 h-7 text-white drop-shadow-sm" />
          </div>
          <div className="flex-1">
            <Badge className="bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-[10px] font-mono tracking-widest mb-2">WORLD-FIRST</Badge>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 drop-shadow-[0_2px_18px_rgba(16,185,129,0.25)]">
              <span className="text-white">Conversion-Aware </span><span className="text-emerald-400">ROI</span>
            </h1>
          </div>
        </div>
        <p className="text-lg text-slate-600 max-w-3xl leading-relaxed">
          Joins your AI cost events with conversion/retention events. Every feature gets a verdict — <span className="text-emerald-300 font-semibold">PROFITABLE</span>, <span className="text-amber-300 font-semibold">BREAK-EVEN</span>, or <span className="text-rose-300 font-semibold">KILL</span>. <span className="text-slate-900 font-semibold">Stop optimizing features that lose money.</span>
        </p>
      </div>

      {/* Week-1 ROI Report status (auto-email after first pipeline connect) */}
      <div className="mb-6">
        <Week1RoiCard />
      </div>

      {/* Landing demo walkthrough funnel (Feb 23, 2026 — surfaces /api/landing-demo/summary) */}
      <div className="mb-6">
        <DemoFunnelTile />
      </div>

      {/* Pilot → Paid auto-conversion proposal (7 days after Week-1 PDF) */}
      <div className="mb-6">
        <PilotToPaidCard />
      </div>

      {/* PostHog active pipeline wizard (P1 — Feb 19, 2026) */}
      <div className="mb-6">
        <PostHogPipelineWizard onBackfillComplete={refresh} />
      </div>

      {/* Mixpanel active pipeline wizard (P1 fast-follow — Feb 19, 2026) */}
      <div className="mb-6">
        <MixpanelPipelineWizard onBackfillComplete={refresh} />
      </div>

      {/* Amplitude active pipeline wizard (P1 #2 — Feb 19, 2026) */}
      <div className="mb-6">
        <AmplitudePipelineWizard onBackfillComplete={refresh} />
      </div>

      {/* Segment adapter (webhook-based, P1 #3 — Feb 19, 2026) */}
      <div className="mb-6">
        <SegmentAdapterWizard />
      </div>

      {/* Heap.io adapter (webhook-based, P1 #5 — Feb 19, 2026) */}
      <div className="mb-8">
        <HeapAdapterWizard />
      </div>

      {/* Summary */}
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="conv-summary-cards">
          <Card className="bg-slate-950 text-slate-100 border-emerald-500/20"><CardContent className="p-4"><div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-2">Features Tracked</div><div className="text-3xl font-bold text-white tabular-nums">{report.rows?.length || 0}</div></CardContent></Card>
          <Card className="bg-slate-950 text-slate-100 border-emerald-500/20"><CardContent className="p-4"><div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-2">Total Cost / {days}d</div><div className="text-3xl font-bold text-white tabular-nums">${report.total_cost_usd}</div></CardContent></Card>
          <Card className="bg-slate-950 text-slate-100 border-emerald-400/30"><CardContent className="p-4"><div className="text-[10px] font-bold text-emerald-300 uppercase tracking-widest mb-2">Attributed Revenue</div><div className="text-3xl font-bold text-emerald-200 tabular-nums">${report.total_value_usd}</div></CardContent></Card>
          <Card className="bg-slate-950 text-slate-100 border-emerald-400/40"><CardContent className="p-4"><div className="text-[10px] font-bold text-emerald-300 uppercase tracking-widest mb-2">Fleet ROI</div><div className="text-3xl font-bold text-emerald-200 tabular-nums">{report.fleet_roi_multiplier !== null ? `${report.fleet_roi_multiplier}×` : '—'}</div></CardContent></Card>
        </div>
      )}

      {/* Kill list */}
      {killList && killList.kill_count > 0 && (
        <Card className="bg-slate-950 text-slate-100 border-rose-500/30 mb-6">
          <CardHeader>
            <CardTitle className="text-base text-white flex items-center gap-2"><Skull className="w-5 h-5 text-rose-400" /> Kill list <span className="text-xs text-cyan-300 font-normal">({killList.kill_count})</span></CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-3 text-sm text-rose-300">Cumulative monthly waste: <span className="font-bold text-rose-200">${killList.monthly_waste_usd}</span> · Annual recoverable: <span className="font-bold text-rose-200">${killList.annual_recoverable_usd.toLocaleString()}</span></div>
            <div className="space-y-2">
              {killList.candidates.map((c, i) => (
                <div key={i} className="flex items-center justify-between rounded-md border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-sm">
                  <div className="text-white font-medium">{c.feature_slug}</div>
                  <div className="text-xs text-slate-400">
                    cost ${c.cost_usd} · value ${c.value_usd} · ROI {c.roi_multiplier !== null ? `${c.roi_multiplier}×` : '—'}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ingest mini-form */}
      <Card className="bg-slate-950 text-slate-100 border-emerald-500/20 mb-6">
        <CardHeader><CardTitle className="text-base text-white flex items-center gap-2"><Plus className="w-4 h-4 text-emerald-400" /> Log a conversion event</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <input type="text" placeholder="feature_slug" value={newEvent.feature_slug} onChange={(e) => setNewEvent({ ...newEvent, feature_slug: e.target.value })} className="bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" data-testid="conv-feature-input" />
            <select value={newEvent.outcome_type} onChange={(e) => setNewEvent({ ...newEvent, outcome_type: e.target.value })} className="bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white">
              <option value="conversion">Conversion</option>
              <option value="retention">Retention</option>
              <option value="upgrade">Upgrade</option>
              <option value="signup">Signup</option>
              <option value="churn">Churn</option>
            </select>
            <input type="number" placeholder="value_usd" value={newEvent.value_usd || ''} onChange={(e) => setNewEvent({ ...newEvent, value_usd: Number(e.target.value) })} className="bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" />
            <Button onClick={submitEvent} disabled={seedBusy || !newEvent.feature_slug} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="conv-submit-btn">{seedBusy ? 'Logging…' : 'Log event'}</Button>
          </div>
        </CardContent>
      </Card>

      {/* ROI table */}
      <Card className="bg-slate-950 text-slate-100 border-emerald-500/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base text-white flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-400" /> Per-feature ROI report</CardTitle>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="bg-slate-800 border border-slate-700 rounded-md px-3 py-1 text-xs text-white">
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">{[0, 1, 2].map(i => <div key={i} className="h-10 bg-slate-800 rounded animate-pulse" />)}</div>
          ) : !report || !report.rows || report.rows.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-400">No data yet. Log a conversion event above + fire some AI cost events to see ROI.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase text-cyan-300 border-b border-slate-800">
                  <tr><th className="text-left py-2">Feature</th><th className="text-right">Cost</th><th className="text-right">Calls</th><th className="text-right">Value</th><th className="text-right">Conv</th><th className="text-right">ROI</th><th className="text-right">Verdict</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {report.rows.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-800">
                      <td className="py-2 text-white">{r.feature_slug}</td>
                      <td className="py-2 text-right font-mono text-slate-400">${r.cost_usd}</td>
                      <td className="py-2 text-right text-slate-400">{r.call_count}</td>
                      <td className="py-2 text-right text-emerald-300">${r.value_usd}</td>
                      <td className="py-2 text-right text-slate-400">{r.conversion_count}</td>
                      <td className="py-2 text-right text-white font-mono">{r.roi_multiplier !== null ? `${r.roi_multiplier}×` : '—'}</td>
                      <td className="py-2 text-right"><VerdictPill verdict={r.verdict} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
